import React from 'react';
import { StyleSheet, View, ViewProps } from 'react-native';

import { RadioGroup } from '../../../components';
import { Checkbox } from '../../../core-ui';
import { colors, spacing } from '../constants/theme';
import { Text, TextInput } from '../core-ui';
import { UploadFieldInput } from '../scenes/programParticipant/UploadFileInput';

export enum QuestionType {
  FREE_TEXT = 'FREE_TEXT',
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  MULTI_CHOICE = 'MULTI_CHOICE',
  FILE_UPLOAD = 'FILE_UPLOAD',
}

export type QuestionOption = {
  label: string;
  value: string;
};

type BaseProps = {
  'data-testid'?: string;
  index?: number;
  question: string;
  required?: boolean;
  disabled?: boolean;
  containerStyle?: ViewProps['style'];
};

export type ChecklistQuestionProps =
  | (BaseProps & {
      type: QuestionType.FREE_TEXT;
      placeholder?: string;
      answer: string;
      onChangeAnswer: (value: string) => void;
    })
  | (BaseProps & {
      type: QuestionType.SINGLE_CHOICE;
      options: Array<QuestionOption>;
      horizontal?: boolean;
      answer: string;
      onChangeAnswer: (value: string) => void;
    })
  | (BaseProps & {
      type: QuestionType.MULTI_CHOICE;
      options: Array<QuestionOption>;
      answer: Array<string>;
      onChangeAnswer: (value: Array<string>) => void;
    })
  | (BaseProps & {
      type: QuestionType.FILE_UPLOAD;
      inputAccept?: string;
      answer: string;
      onChangeAnswer: (value: string) => void;
    });

export function ChecklistQuestion(props: ChecklistQuestionProps) {
  let { index, question, required, disabled, containerStyle } = props;
  let label = `${index != null ? `${index}. ` : ''}${question}`;

  if (props.type === QuestionType.FILE_UPLOAD) {
    return (
      <View style={[styles.container, containerStyle]}>
        <UploadFieldInput
          title={label}
          titleStyle={{ bold: true }}
          fileUrl={props.answer}
          onFileSelected={props.onChangeAnswer}
          disabled={disabled}
          required={required}
          inputAccept={props.inputAccept}
        />
      </View>
    );
  }

  return (
    <View style={[styles.container, containerStyle]}>
      <Text bold style={styles.label}>
        {label}
        {required && (
          <Text bold style={styles.requiredMark}>
            {' *'}
          </Text>
        )}
      </Text>

      {props.type === QuestionType.FREE_TEXT && (
        <TextInput
          data-testid={props['data-testid']}
          containerStyle={styles.answer}
          value={props.answer}
          onChangeText={props.onChangeAnswer}
          placeholder={props.placeholder}
          disabled={disabled}
        />
      )}

      {props.type === QuestionType.SINGLE_CHOICE && (
        <RadioGroup
          containerStyle={styles.answer}
          horizontal={props.horizontal ?? true}
          disabled={disabled}
          options={props.options}
          selectedOption={props.answer}
          setSelectedOption={props.onChangeAnswer}
        />
      )}

      {props.type === QuestionType.MULTI_CHOICE &&
        (disabled ? (
          <Text style={styles.answer} color="grey">
            {props.options
              .filter(({ value }) => props.answer.includes(value))
              .map(({ label }) => label)
              .join(', ') || '-'}
          </Text>
        ) : (
          <View style={[styles.answer, styles.multiChoiceOptions]}>
            {props.options.map(({ label, value }) => {
              let checked = props.answer.includes(value);
              return (
                <View key={value} style={styles.multiChoiceOption}>
                  <Checkbox
                    label={label}
                    checked={checked}
                    onPress={(check) => {
                      let nextAnswer = check
                        ? [...props.answer, value]
                        : props.answer.filter((v) => v !== value);
                      props.onChangeAnswer(nextAnswer);
                    }}
                  />
                </View>
              );
            })}
          </View>
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: spacing.s,
    paddingHorizontal: spacing.m,
  },
  label: {
    marginBottom: spacing.xxs,
  },
  requiredMark: {
    color: colors.text.red,
  },
  answer: {
    marginTop: spacing.xxs,
    width: '100%',
  },
  multiChoiceOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  multiChoiceOption: {
    marginRight: spacing.m,
    marginBottom: spacing.xxs,
  },
});
